import { useCallback, useState } from 'react';
import { Colors, Flex, IconChevronDown, Typography } from '@banhala/ui-kit';
import { type NavigationProp, useNavigation } from '@react-navigation/native';
import type { SortingGoodsListProps } from './types';
import { bridge } from '../../../../utils';
import { useFilterListAtom } from '../FilterList/Provider/filterList/useFilterListAtom';
import { useSortingBottomSheet } from './useSortingBottomSheet';
import type { SortingItem } from './types';
import {
  useBottomSheetRoute,
  useIsInFragmentScreens,
} from '../../../../hooks';

export const SortingGoodsList = ({
  body,
  logging,
  scrollToFilterList,
}: SortingGoodsListProps) => {
  const navigation = useNavigation<
    NavigationProp<{
      [route: string]: Record<string, string[] | undefined>;
    }>
  >();
  const { setFilterListAtom } = useFilterListAtom();
  const [selectedText, setSelectedText] = useState(
    body?.item.selected_item_text ?? '',
  );
  const { show: showSortingBottomSheet } = useSortingBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();

  const handlePressSort = useCallback(async () => {
    if (!body) return;

    const result = isInFragmentScreens
      ? await openBottomSheetRoute<
          { selectedText: string; possibleItems: SortingItem[] },
          { selectedItem: SortingItem }
        >('sorting-picker', {
          selectedText,
          possibleItems: body.item.possible_items,
        })
      : await showSortingBottomSheet({
          selectedText,
          possibleItems: body.item.possible_items,
        });

    if (result.type !== 'confirm') return;

    const { selectedItem } = result.data;

    bridge.logAnalyticsEvent({
      name: 'CLICK_SORT',
      analytics: {
        ...logging?.analytics,
        ...body.logging?.analytics,
        ...selectedItem.logging?.analytics,
      },
      ably_only_log: { ...logging?.ably_only_log, ...body.logging?.ably_only_log, ...selectedItem.logging?.ably_only_log },
    });

    setSelectedText(selectedItem.text);
    scrollToFilterList();

    if (body.item.query_key) {
      setFilterListAtom(prev => ({
        ...prev,
        [body.item.query_key]: [selectedItem.query_value],
      }));
      navigation.setParams({
        [body.item.query_key]: [selectedItem.query_value],
      });
    }
  }, [
    body,
    isInFragmentScreens,
    logging,
    openBottomSheetRoute,
    selectedText,
    showSortingBottomSheet,
    scrollToFilterList,
    setFilterListAtom,
    navigation,
  ]);

  if (!body) return null;

  return (
    <Flex.Row
      alignItems="center"
      justifyContent="space-between"
      padding={{ horizontal: 16 }}
      height={52}
    >
      <Flex flex={1}>
        <Typography type="subtitle1" color="content_primary">
          {body.item.title.replace(/<[^>]*>/g, '')}
        </Typography>
      </Flex>

      <Flex.Row
        alignItems="center"
        gap={4}
        onPress={handlePressSort}
        padding={{ horizontal: 12 }}
      >
        <Typography type="body3" color="content_secondary">
          {selectedText}
        </Typography>
        <IconChevronDown size={12} fill={Colors.content_secondary} />
      </Flex.Row>
    </Flex.Row>
  );
};
